import { useEffect, useState } from 'react';

/* ─── helpers ──────────────────────────────────────────────── */
const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || `${response.status} ${response.statusText}`);
    }
    return response.json();
};

const emptyBien = {
    nom: '', categorie: '', etat: '', numero_serie: '',
    marque: '', modele: '', couleur: '',
    date_acquisition: '', valeur: '', description: '',
    nombre: 1,
};

/* ─── etat badge colours ────────────────────────────────────── */
const etatBadge = (etat = '') => {
    const e = etat.toLowerCase();
    if (e.includes('bon') || e.includes('neuf'))
        return { bg: '#dcfce7', color: '#16a34a', dot: '#16a34a' };
    if (e.includes('hors') || e.includes('inactif'))
        return { bg: '#fee2e2', color: '#dc2626', dot: '#dc2626' };
    if (e.includes('répar') || e.includes('usagé'))
        return { bg: '#fef9c3', color: '#ca8a04', dot: '#ca8a04' };
    return { bg: '#f1f5f9', color: '#64748b', dot: '#94a3b8' };
};

const Badge = ({ label }) => {
    const s = etatBadge(label);
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
            background: s.bg, color: s.color,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
            {label}
        </span>
    );
};

/* ─── inline styles ─────────────────────────────────────────── */
const css = {
    root: {
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        color: 'var(--text, #0f172a)',
        fontSize: 14,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 24,
    },
    statCard: () => ({
        background: '#fff',
        border: `1px solid #e2e8f0`,
        borderRadius: 12,
        padding: '16px 20px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
    }),
    statAccentBar: (color) => ({
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, background: color, borderRadius: '12px 12px 0 0',
    }),
    statIcon: (bg) => ({
        width: 36, height: 36, borderRadius: 8,
        background: bg, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 18, marginBottom: 10,
    }),
    statLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5 },
    statValue: { fontSize: 26, fontWeight: 700, lineHeight: 1.1, marginTop: 2 },
    statSub: { fontSize: 11, color: '#94a3b8', marginTop: 3 },
    toolbar: {
        display: 'flex', gap: 8, flexWrap: 'wrap',
        alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14,
    },
    toolbarLeft: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flex: 1 },
    input: {
        height: 36, padding: '0 12px', borderRadius: 8,
        border: '1px solid #e2e8f0', fontSize: 13,
        background: '#fff', outline: 'none', width: '100%',
        boxSizing: 'border-box', color: '#0f172a',
        transition: 'border-color .15s',
    },
    select: {
        height: 36, padding: '0 10px', borderRadius: 8,
        border: '1px solid #e2e8f0', fontSize: 13,
        background: '#fff', outline: 'none',
        color: '#0f172a', cursor: 'pointer',
    },
    textarea: {
        padding: '10px 12px', borderRadius: 8,
        border: '1px solid #e2e8f0', fontSize: 13,
        background: '#fff', outline: 'none', width: '100%',
        boxSizing: 'border-box', color: '#0f172a', resize: 'vertical',
        fontFamily: 'inherit',
    },
    btnPrimary: {
        height: 36, padding: '0 16px', borderRadius: 8,
        background: '#2563eb', color: '#fff', fontWeight: 600,
        fontSize: 13, border: 'none', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'background .15s',
        whiteSpace: 'nowrap',
    },
    btnSecondary: {
        height: 36, padding: '0 14px', borderRadius: 8,
        background: '#f1f5f9', color: '#475569', fontWeight: 600,
        fontSize: 13, border: '1px solid #e2e8f0', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        transition: 'background .15s',
    },
    btnGhost: {
        height: 30, padding: '0 10px', borderRadius: 6,
        background: 'transparent', color: '#64748b', fontWeight: 500,
        fontSize: 12, border: '1px solid #e2e8f0', cursor: 'pointer',
        transition: 'all .12s',
    },
    btnDanger: {
        height: 36, padding: '0 14px', borderRadius: 8,
        background: '#fee2e2', color: '#dc2626', fontWeight: 600,
        fontSize: 13, border: '1px solid #fecaca', cursor: 'pointer',
    },
    tableWrap: {
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        padding: '10px 14px', textAlign: 'left',
        fontSize: 11, fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: .5,
        borderBottom: '1px solid #f1f5f9', background: '#fafafa',
        whiteSpace: 'nowrap',
    },
    thRight: {
        padding: '10px 14px', textAlign: 'right',
        fontSize: 11, fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: .5,
        borderBottom: '1px solid #f1f5f9', background: '#fafafa',
    },
    td: {
        padding: '12px 14px', borderBottom: '1px solid #f8fafc',
        verticalAlign: 'middle',
    },
    tdName: {
        background: 'none', border: 'none', padding: 0,
        color: '#1e40af', fontWeight: 600, fontSize: 13,
        cursor: 'pointer', textAlign: 'left',
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '16px 20px',
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
    },
    formLabel: {
        display: 'flex', flexDirection: 'column', gap: 5,
        fontSize: 13, fontWeight: 600, color: '#475569',
    },
    errorText: { fontSize: 11, color: '#dc2626', fontWeight: 500 },
    pageHeader: {
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 16, gap: 12,
        flexWrap: 'wrap',
    },
    h2: { fontSize: 20, fontWeight: 700, margin: 0 },
    detailCard: {
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, padding: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
    },
    detailGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '14px 24px',
    },
    detailRow: { display: 'flex', flexDirection: 'column', gap: 3 },
    detailLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5 },
    detailValue: { fontSize: 14, fontWeight: 500, color: '#0f172a' },
    alertSuccess: {
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        borderRadius: 8, padding: '10px 14px',
        color: '#15803d', fontWeight: 500, fontSize: 13, marginBottom: 14,
    },
    alertError: {
        background: '#fef2f2', border: '1px solid #fecaca',
        borderRadius: 8, padding: '10px 14px',
        color: '#dc2626', fontWeight: 500, fontSize: 13, marginBottom: 14,
    },
    emptyState: {
        textAlign: 'center', padding: '48px 24px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    },
    emptyIcon: {
        width: 56, height: 56, borderRadius: 16,
        background: '#f1f5f9', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 28, marginBottom: 4,
    },
    paginationBar: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderTop: '1px solid #f1f5f9',
        fontSize: 12, color: '#64748b',
    },
    paginationBtn: {
        width: 28, height: 28, borderRadius: 6,
        border: '1px solid #e2e8f0', background: '#fff',
        cursor: 'pointer', fontWeight: 700, fontSize: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    searchBox: { position: 'relative', minWidth: 220, flex: 1, maxWidth: 320 },
    searchIcon: {
        position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
        color: '#94a3b8', pointerEvents: 'none', fontSize: 14,
    },
    searchInput: {
        height: 36, padding: '0 12px 0 32px', borderRadius: 8,
        border: '1px solid #e2e8f0', fontSize: 13,
        background: '#fff', outline: 'none', width: '100%',
        boxSizing: 'border-box', color: '#0f172a',
    },
};

/* ─── stat cards config ─────────────────────────────────────── */
const STAT_CARDS = [
    { key: 'total',        label: 'Total biens',   sub: "dans l'inventaire", icon: '📦', color: '#f97316', bg: '#fff7ed' },
    { key: 'valeur_total', label: 'Valeur totale', sub: 'valeur estimée',    icon: '💰', color: '#22c55e', bg: '#f0fdf4' },
    { key: 'categories',   label: 'Catégories',    sub: 'types de biens',    icon: '🗂️', color: '#3b82f6', bg: '#eff6ff' },
    { key: 'hors_service', label: 'Hors service',  sub: 'biens inactifs',    icon: '⚠️', color: '#ef4444', bg: '#fef2f2' },
];

/* ─── Field component (OUTSIDE BiensTab to prevent remount on re-render) ── */
const Field = ({ label, name, type = 'text', fullWidth = false, form, setForm, errors, children }) => {
    const fieldValue = form[name] ?? '';
    return (
        <label style={{ ...css.formLabel, ...(fullWidth ? { gridColumn: '1 / -1' } : {}) }}>
            {label}
            {children || (
                <input
                    id={`bien-${name}`}
                    name={name}
                    autoComplete="off"
                    spellCheck="false"
                    style={css.input}
                    type={type}
                    value={fieldValue}
                    onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                    step={type === 'number' ? '0.01' : undefined}
                />
            )}
            {errors[name] && <span style={css.errorText}>⚠ {errors[name][0]}</span>}
        </label>
    );
};

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════ */
export default function BiensTab() {
    const [mode, setMode] = useState('list');
    const [biens, setBiens] = useState([]);
    const [selectedBien, setSelectedBien] = useState(null);
    const [stats, setStats] = useState({ total: 0, valeur_total: 0, hors_service: 0, categories: 0 });
    const [categories, setCategories] = useState({});
    const [etats, setEtats] = useState({});
    const [filters, setFilters] = useState({ q: '', categorie: '', etat: '' });
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 20 });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(emptyBien);
    const [notification, setNotification] = useState({ msg: '', type: '' });
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [newCategoryOpen, setNewCategoryOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');

    /* ── routing ── */
    const parseUrlMode = async () => {
        const path = window.location.pathname.replace(/\/+$/, '');
        const parts = path.split('/').filter(Boolean);
        if (parts.length >= 2 && parts[0] === 'biens') {
            if (parts[1] === 'create') { setMode('create'); return; }
            if (parts[2] === 'edit' && parts[1]) { await loadBien(parts[1], 'edit'); return; }
            if (parts[1]) { await loadBien(parts[1], 'show'); return; }
        }
        setMode('list');
    };

    const pushUrl = (newMode, bien = null) => {
        let url = '/biens';
        if (newMode === 'create') url = '/biens/create';
        if (newMode === 'show' && bien) url = `/biens/${bien.id}`;
        if (newMode === 'edit' && bien) url = `/biens/${bien.id}/edit`;
        window.history.pushState(null, '', url);
    };

    const navigate = async (newMode, bien = null) => {
        setErrors({});
        setNotification({ msg: '', type: '' });
        if (newMode === 'list') {
            setMode('list'); setSelectedBien(null); setForm(emptyBien);
            pushUrl('list'); await load(1); return;
        }
        if (newMode === 'create') {
            setMode('create'); setSelectedBien(null); setForm(emptyBien);
            pushUrl('create'); return;
        }
        if (bien) { await loadBien(bien.id, newMode); pushUrl(newMode, bien); }
    };

    const load = async (page = 1) => {
        setLoading(true);
        const params = new URLSearchParams({ ...filters, page });
        try {
            const json = await fetchJson('/api/biens?' + params.toString());
            setBiens(json.data || []);
            setMeta(json.meta || {});
            setStats(json.stats || {});
            // Normalize categories returned by API into { key: label }
            if (json.categories) {
                if (Array.isArray(json.categories)) {
                    if (json.categories.length > 0 && typeof json.categories[0] === 'string') {
                        const map = {};
                        json.categories.forEach((name) => { map[name] = name; });
                        setCategories(map);
                    } else if (json.categories.length > 0 && typeof json.categories[0] === 'object') {
                        const map = {};
                        json.categories.forEach((c) => { map[c.name] = c.name; });
                        setCategories(map);
                    } else {
                        setCategories({});
                    }
                } else if (typeof json.categories === 'object') {
                    setCategories(json.categories);
                } else {
                    setCategories({});
                }
            } else {
                try {
                    const cats = await fetchJson('/api/biens/categories');
                    const map = {};
                    cats.forEach((c) => { map[c.name] = c.name; });
                    setCategories(map);
                } catch (e) {
                    console.warn('Impossible de charger les catégories', e);
                    setCategories({});
                }
            }
            setEtats(json.etats || {});
        } catch (e) {
            console.error('Erreur chargement biens', e);
        } finally {
            setLoading(false);
        }
    };

    const loadBien = async (id, targetMode) => {
        setLoading(true);
        try {
            const bien = await fetchJson(`/api/biens/${id}`);
            setSelectedBien(bien);
            setForm({
                nom: bien.nom || '',
                categorie: bien.categorie || '',
                etat: bien.etat || '',
                numero_serie: bien.numero_serie || '',
                marque: bien.marque || '',
                modele: bien.modele || '',
                couleur: bien.couleur || '',
                nombre: bien.nombre ?? 1,
                date_acquisition: bien.date_acquisition ? bien.date_acquisition.substring(0, 10) : '',
                valeur: bien.valeur ?? '',
                description: bien.description || '',
            });
            setMode(targetMode);
        } catch (e) {
            console.error('Impossible de charger le bien', e);
            navigate('list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(1);
        parseUrlMode();
        window.addEventListener('popstate', parseUrlMode);
        return () => window.removeEventListener('popstate', parseUrlMode);
    }, []);

    /* ── form submit ── */
    const submitForm = async (e) => {
        e.preventDefault();
        setLoading(true); setErrors({}); setNotification({ msg: '', type: '' });
        try {
            const payload = { ...form };
            if (payload.valeur === '') delete payload.valeur;
            if (payload.date_acquisition === '') delete payload.date_acquisition;

            if (mode === 'create') {
                await fetchJson('/api/biens', { method: 'POST', body: JSON.stringify(payload) });
                setNotification({ msg: 'Bien créé avec succès.', type: 'success' });
                navigate('list');
            } else if (mode === 'edit' && selectedBien) {
                await fetchJson(`/api/biens/${selectedBien.id}`, { method: 'PUT', body: JSON.stringify(payload) });
                setNotification({ msg: 'Bien mis à jour.', type: 'success' });
                navigate('show', selectedBien);
            }
        } catch (error) {
            if (error.message?.startsWith('The given data was invalid.')) {
                const response = await error.response?.json().catch(() => null);
                setErrors(response?.errors || {});
            } else {
                console.error(error);
                setNotification({ msg: 'Une erreur est survenue.', type: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const removeBien = async () => {
        if (!selectedBien || !window.confirm(`Supprimer « ${selectedBien.nom} » ? Cette action est irréversible.`)) return;
        setLoading(true);
        try {
            await fetchJson(`/api/biens/${selectedBien.id}`, { method: 'DELETE' });
            setNotification({ msg: 'Bien supprimé.', type: 'success' });
            navigate('list');
        } catch (error) {
            console.error(error);
            setNotification({ msg: 'Impossible de supprimer le bien.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const createNewCategory = async () => {
        if (!newCategoryName.trim()) {
            alert('Veuillez entrer un nom de catégorie.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetchJson('/api/biens/categories', {
                method: 'POST',
                body: JSON.stringify({ name: newCategoryName })
            });
            setCategories((prev) => ({ ...prev, [newCategoryName]: newCategoryName }));
            setNotification({ msg: 'Catégorie créée avec succès.', type: 'success' });
            setNewCategoryOpen(false);
            setNewCategoryName('');
        } catch (error) {
            console.error(error);
            setNotification({ msg: error.message || 'Erreur lors de la création de la catégorie.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const printAllUnprinted = async () => {
        const unprinted = biens.filter((bien) => !bien.imprime);
        if (unprinted.length === 0) {
            alert('Aucun bien non imprimé.');
            return;
        }

        setLoading(true);
        try {
            const printedItems = [];
            for (const bien of unprinted) {
                try {
                    const printed = await fetchJson(`/api/biens/${bien.id}/print`, { method: 'POST' });
                    printedItems.push(printed);
                } catch (e) {
                    console.warn(`Impossible d'imprimer le bien ${bien.id}:`, e);
                }
            }

            if (printedItems.length === 0) {
                alert('Aucune impression effectuée.');
                return;
            }

            const content = `<!doctype html><html><head><meta charset="utf-8"><title>Impression des biens</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:20px}h1{font-size:20px;margin-bottom:16px}section{margin-bottom:28px;border-bottom:1px solid #ddd;padding-bottom:12px}p{font-size:14px;margin:4px 0}</style></head><body><h1>Impression des biens</h1>${printedItems.map((printed) => `
                <section><p><strong>Référence :</strong> ${printed.reference || '—'}</p>
                <p><strong>Nom :</strong> ${printed.nom}</p>
                <p><strong>Catégorie :</strong> ${printed.categorie || '—'}</p>
                <p><strong>Couleur :</strong> ${printed.couleur || '—'}</p></section>
            `).join('')}</body></html>`;
            const w = window.open('', '_blank');
            if (w) {
                w.document.write(content);
                w.document.close();
                w.focus();
                setTimeout(() => w.print(), 500);
            }

            setBiens((prev) => prev.map((bien) => {
                const updated = printedItems.find((item) => item.id === bien.id);
                return updated ? updated : bien;
            }));
        } catch (error) {
            console.error(error);
            alert('Erreur lors de l\'impression des biens');
        } finally {
            setLoading(false);
        }
    };

    /* ── stat cards ── */
    const renderStats = () => (
        <div style={css.statsGrid}>
            {STAT_CARDS.map(({ key, label, sub, icon, color, bg }) => {
                const raw = stats[key];
                const display = key === 'valeur_total'
                    ? (raw ? new Intl.NumberFormat('fr-FR').format(raw) + ' Ar' : '—')
                    : (raw ?? 0);
                return (
                    <div key={key} style={css.statCard(color)}>
                        <div style={css.statAccentBar(color)} />
                        <div style={css.statIcon(bg)}>{icon}</div>
                        <div style={css.statLabel}>{label}</div>
                        <div style={{ ...css.statValue, color }}>{display}</div>
                        <div style={css.statSub}>{sub}</div>
                    </div>
                );
            })}
        </div>
    );

    const availabilityLabel = (key) => {
        return ({
            available: 'Disponible',
            unavailable: 'Indisponible',
            destroyed: 'Détruit',
            transferred: 'Transféré',
            recovered: 'Récupéré',
        }[key] || key);
    };

    const AvailabilityBadge = ({ status }) => {
        const label = availabilityLabel(status);
        const style = {
            available: { bg: '#ecfdf5', color: '#059669' },
            unavailable: { bg: '#fff1f2', color: '#dc2626' },
            destroyed: { bg: '#fff1f2', color: '#991b1b' },
            transferred: { bg: '#f0f9ff', color: '#0369a1' },
            recovered: { bg: '#f0f9f0', color: '#065f46' },
        }[status] || { bg: '#f1f5f9', color: '#64748b' };
        return (
            <span style={{ padding: '4px 8px', borderRadius: 999, background: style.bg, color: style.color, fontWeight: 700, fontSize: 12 }}>{label}</span>
        );
    };

    const performAvailabilityAction = async (action) => {
        if (!selectedBien) return;
        let url = `/api/biens/${selectedBien.id}`;
        let body = null;

        if (action === 'unavailable') {
            const reason = window.prompt('Raison pour marquer indisponible :', 'Perdu / prêt / autre');
            if (reason === null) return;
            url += '/mark-unavailable';
            body = { reason };
        } else if (action === 'destroyed') {
            const confirm = window.confirm('Confirmer la destruction de ce bien ? Cette action peut être réversible via l\'historique mais marquera le bien comme détruit.');
            if (!confirm) return;
            const reason = window.prompt('Raison de la destruction :', 'Destruction');
            url += '/mark-destroyed';
            body = { reason };
        } else if (action === 'transferred') {
            const reason = window.prompt('Raison / destinataire pour le transfert :', 'Transféré');
            if (reason === null) return;
            url += '/mark-transferred';
            body = { reason };
        } else if (action === 'restore') {
            url += '/restore';
        } else {
            return;
        }

        setLoading(true);
        try {
            const res = await fetchJson(url, body ? { method: 'POST', body: JSON.stringify(body) } : { method: 'POST' });
            const bien = res.bien || res;
            setSelectedBien(bien);
            setBiens((prev) => prev.map((b) => b.id === bien.id ? bien : b));
            setNotification({ msg: res.message || 'Action effectuée', type: 'success' });
        } catch (e) {
            console.error(e);
            setNotification({ msg: e.message || 'Erreur', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    /* ── list ── */
    const renderList = () => (
        <>
            <div style={css.toolbar}>
                <div style={css.toolbarLeft}>
                    <div style={css.searchBox}>
                        <span style={css.searchIcon}>🔍</span>
                        <input
                            style={css.searchInput}
                            placeholder="Nom, marque, N° série…"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && load(1)}
                        />
                    </div>
                    <select
                        style={css.select}
                        value={filters.categorie}
                        onChange={(e) => setFilters((f) => ({ ...f, categorie: e.target.value }))}
                    >
                        <option value="">Toutes catégories</option>
                        {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select
                        style={css.select}
                        value={filters.etat}
                        onChange={(e) => setFilters((f) => ({ ...f, etat: e.target.value }))}
                    >
                        <option value="">Tous les états</option>
                        {Object.entries(etats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button style={css.btnSecondary} type="button" onClick={() => load(1)}>
                        Filtrer
                    </button>
                    <button style={css.btnSecondary} type="button" onClick={printAllUnprinted}>
                        Imprimer non imprimés
                    </button>
                    <button style={css.btnSecondary} type="button" onClick={() => setNewCategoryOpen(true)}>
                        📁 Nouvelle catégorie
                    </button>
                </div>
                <button style={css.btnPrimary} type="button" onClick={() => navigate('create')}>
                    + Ajouter un bien
                </button>
            </div>

            <div style={css.tableWrap}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                        <span style={{ fontSize: 24 }}>⏳</span>
                        <p style={{ marginTop: 8, fontSize: 13 }}>Chargement…</p>
                    </div>
                ) : biens.length === 0 ? (
                    <div style={css.emptyState}>
                        <div style={css.emptyIcon}>🏢</div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Aucun bien trouvé</h3>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                            Commencez par ajouter votre premier bien à l'inventaire.
                        </p>
                        <button style={{ ...css.btnPrimary, marginTop: 8 }} type="button" onClick={() => navigate('create')}>
                            + Ajouter un bien
                        </button>
                    </div>
                ) : (
                    <table style={css.table}>
                        <thead>
                                    <tr>
                                        <th style={css.th}>Réf</th>
                                        <th style={css.th}>Bien</th>
                                        <th style={css.th}>Catégorie</th>
                                        <th style={css.th}>État</th>
                                        <th style={css.th}>Disponibilité</th>
                                        <th style={css.th}>Nombre</th>
                                        <th style={css.th}>Valeur unit.</th>
                                        <th style={css.th}>Acquisition</th>
                                        <th style={css.th}>Impression</th>
                                        <th style={css.thRight}>Actions</th>
                                    </tr>
                                </thead>
                        <tbody>
                            {biens.map((bien, idx) => (
                                <tr
                                    key={bien.id}
                                    style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}
                                >
                                    <td style={css.td}>
                                        <div style={{ fontFamily: 'monospace', color: '#64748b', fontSize: 12 }}>{bien.reference || '—'}</div>
                                    </td>
                                    <td style={css.td}>
                                        <button
                                            style={css.tdName}
                                            type="button"
                                            onClick={() => navigate('show', bien)}
                                        >
                                            {bien.nom}
                                        </button>
                                        {bien.marque && (
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                                {bien.marque}{bien.modele ? ' · ' + bien.modele : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td style={css.td}>
                                        {bien.categorie
                                            ? <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{bien.categorie}</span>
                                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={css.td}>
                                        {bien.etat ? <Badge label={bien.etat} /> : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={css.td}>
                                        {bien.availability_status ? <AvailabilityBadge status={bien.availability_status} /> : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={{ ...css.td, textAlign: 'center', fontWeight: 700 }}>
                                        {bien.nombre && Number(bien.nombre) > 1 ? (
                                            <span style={{ background: '#eef2ff', color: '#1e3a8a', padding: '6px 10px', borderRadius: 999 }}>{bien.nombre}</span>
                                        ) : (
                                            <span style={{ color: '#64748b' }}>{bien.nombre ?? 1}</span>
                                        )}
                                    </td>
                                    <td style={{ ...css.td, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                        {bien.valeur
                                            ? new Intl.NumberFormat('fr-FR').format(bien.valeur) + ' Ar'
                                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={{ ...css.td, color: '#94a3b8', fontSize: 12 }}>
                                        {bien.date_acquisition
                                            ? new Date(bien.date_acquisition).toLocaleDateString('fr-FR')
                                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={{ ...css.td, textAlign: 'center' }}>
                                        {bien.imprime ? (
                                            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                                <span style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>Imprimé</span>
                                                <small style={{ color: '#94a3b8' }}>{bien.date_impression ? new Date(bien.date_impression).toLocaleString('fr-FR') : ''}</small>
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: 12, color: '#64748b' }}>Non imprimé</span>
                                        )}
                                    </td>
                                    <td style={{ ...css.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button
                                            style={{ ...css.btnGhost, marginRight: 4 }}
                                            type="button"
                                            onClick={() => navigate('show', bien)}
                                        >
                                            Voir
                                        </button>
                                        <button
                                            style={css.btnGhost}
                                            type="button"
                                            onClick={() => navigate('edit', bien)}
                                        >
                                            Modifier
                                        </button>
                                        <button
                                            style={{ ...css.btnGhost, marginLeft: 6 }}
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await fetchJson(`/api/biens/${bien.id}/history`);
                                                    setHistoryData(res.history || res || []);
                                                    setHistoryOpen(true);
                                                } catch (e) {
                                                    alert('Impossible de charger l\'historique');
                                                }
                                            }}
                                        >Historique</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {meta.last_page > 1 && (
                    <div style={css.paginationBar}>
                        <span>
                            {(meta.current_page - 1) * (meta.per_page || 20) + 1}–
                            {Math.min(meta.current_page * (meta.per_page || 20), meta.total)} sur {meta.total} biens
                        </span>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <button
                                style={{ ...css.paginationBtn, opacity: meta.current_page === 1 ? .35 : 1 }}
                                disabled={meta.current_page === 1}
                                onClick={() => load(meta.current_page - 1)}
                            >‹</button>
                            <span style={{
                                minWidth: 28, textAlign: 'center', fontSize: 13,
                                fontWeight: 700, color: '#1e40af',
                            }}>
                                {meta.current_page}
                            </span>
                            <button
                                style={{ ...css.paginationBtn, opacity: meta.current_page === meta.last_page ? .35 : 1 }}
                                disabled={meta.current_page === meta.last_page}
                                onClick={() => load(meta.current_page + 1)}
                            >›</button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    /* ── form ── */
    const renderForm = () => (
        <div>
            <div style={css.pageHeader}>
                <h2 style={css.h2}>
                    {mode === 'create' ? '➕ Ajouter un bien' : `✏️ Modifier · ${selectedBien?.nom}`}
                </h2>
                <button style={css.btnSecondary} type="button" onClick={() => navigate('list')}>
                    ← Retour à la liste
                </button>
            </div>

            {notification.msg && (
                <div style={notification.type === 'error' ? css.alertError : css.alertSuccess}>
                    {notification.msg}
                </div>
            )}

            <form onSubmit={submitForm}>
                <div style={css.formGrid}>
                    <Field label="Nom *" name="nom" form={form} setForm={setForm} errors={errors} />
                    <Field label="Catégorie *" name="categorie" form={form} setForm={setForm} errors={errors}>
                        <select
                            style={css.select}
                            value={form.categorie}
                            onChange={(e) => setForm((f) => ({ ...f, categorie: e.target.value }))}
                        >
                            <option value="">Sélectionnez</option>
                            {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        {errors.categorie && <span style={css.errorText}>⚠ {errors.categorie[0]}</span>}
                    </Field>
                    <Field label="État *" name="etat" form={form} setForm={setForm} errors={errors}>
                        <select
                            style={css.select}
                            value={form.etat}
                            onChange={(e) => setForm((f) => ({ ...f, etat: e.target.value }))}
                        >
                            <option value="">Sélectionnez</option>
                            {Object.entries(etats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        {errors.etat && <span style={css.errorText}>⚠ {errors.etat[0]}</span>}
                    </Field>
                    <Field label="Numéro de série" name="numero_serie" form={form} setForm={setForm} errors={errors} />
                    <Field label="Marque" name="marque" form={form} setForm={setForm} errors={errors} />
                    <Field label="Modèle" name="modele" form={form} setForm={setForm} errors={errors} />
                    <Field label="Nombre" name="nombre" type="number" form={form} setForm={setForm} errors={errors} />
                    <Field label="Couleur" name="couleur" form={form} setForm={setForm} errors={errors} />
                    <Field label="Date d'acquisition" name="date_acquisition" type="date" form={form} setForm={setForm} errors={errors} />
                    <Field label="Valeur (Ar)" name="valeur" type="number" form={form} setForm={setForm} errors={errors} />
                    <Field label="Description" name="description" fullWidth form={form} setForm={setForm} errors={errors}>
                        <textarea
                            style={css.textarea}
                            rows={4}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                    </Field>
                </div>

                <div style={{
                    display: 'flex', gap: 8, justifyContent: 'flex-end',
                    marginTop: 16, paddingTop: 4,
                }}>
                    <button style={css.btnSecondary} type="button" onClick={() => navigate('list')}>
                        Annuler
                    </button>
                    <button style={css.btnPrimary} type="submit" disabled={loading}>
                        {loading ? '⏳ Enregistrement…' : mode === 'create' ? '✓ Créer le bien' : '✓ Mettre à jour'}
                    </button>
                </div>
            </form>
        </div>
    );

    /* ── show ── */
    const DetailRow = ({ label, value, full = false }) => (
        <div style={{ ...css.detailRow, ...(full ? { gridColumn: '1 / -1' } : {}) }}>
            <span style={css.detailLabel}>{label}</span>
            <span style={css.detailValue}>{value || <span style={{ color: '#cbd5e1' }}>—</span>}</span>
        </div>
    );

    const renderShow = () => (
        <div>
            <div style={css.pageHeader}>
                <div>
                    <h2 style={css.h2}>{selectedBien?.nom}</h2>
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                        {selectedBien?.etat && <Badge label={selectedBien.etat} />}
                        {selectedBien?.availability_status && <AvailabilityBadge status={selectedBien.availability_status} />}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={css.btnSecondary} type="button" onClick={() => navigate('list')}>
                        ← Retour
                    </button>
                    <button style={css.btnGhost} type="button" onClick={() => navigate('edit', selectedBien)}>
                        ✏️ Modifier
                    </button>
                    <button style={css.btnDanger} type="button" onClick={removeBien}>
                        🗑 Supprimer
                    </button>
                    <button style={css.btnGhost} type="button" onClick={async () => {
                        try {
                            const res = await fetchJson(`/api/biens/${selectedBien.id}/history`);
                            setHistoryData(res.history || res || []);
                            setHistoryOpen(true);
                        } catch (e) {
                            alert('Impossible de charger l\'historique');
                        }
                    }}>Historique</button>
                    {selectedBien?.availability_status !== 'available' ? (
                        <button style={css.btnPrimary} type="button" onClick={async () => await performAvailabilityAction('restore')}>Restaurer</button>
                    ) : (
                        <>
                            <button style={css.btnGhost} type="button" onClick={async () => await performAvailabilityAction('unavailable')}>Marquer indisponible</button>
                            <button style={css.btnDanger} type="button" onClick={async () => await performAvailabilityAction('destroyed')}>Détruire</button>
                            <button style={css.btnSecondary} type="button" onClick={async () => await performAvailabilityAction('transferred')}>Transférer</button>
                        </>
                    )}
                </div>
            </div>

            {notification.msg && (
                <div style={notification.type === 'error' ? css.alertError : css.alertSuccess}>
                    {notification.msg}
                </div>
            )}

            <div style={css.detailCard}>
                <div style={css.detailGrid}>
                    <DetailRow label="Catégorie" value={selectedBien?.categorie} />
                    <DetailRow label="Numéro de série" value={selectedBien?.numero_serie} />
                    <DetailRow label="Marque" value={selectedBien?.marque} />
                    <DetailRow label="Modèle" value={selectedBien?.modele} />
                    <DetailRow label="Nombre" value={selectedBien?.nombre ?? 1} />
                    <DetailRow label="Couleur" value={selectedBien?.couleur} />
                    <DetailRow
                        label="Date d'acquisition"
                        value={selectedBien?.date_acquisition
                            ? new Date(selectedBien.date_acquisition).toLocaleDateString('fr-FR', {
                                day: '2-digit', month: 'long', year: 'numeric',
                              })
                            : null}
                    />
                    <DetailRow
                        label="Valeur estimée"
                        value={selectedBien?.valeur
                            ? new Intl.NumberFormat('fr-FR').format(selectedBien.valeur) + ' Ar'
                            : null}
                    />
                    <DetailRow label="Description" value={selectedBien?.description} full />
                </div>
            </div>
        </div>
    );

    /* ── root ── */
    return (
        <div style={css.root}>
            {renderStats()}
            {mode === 'list' && renderList()}
            {(mode === 'create' || mode === 'edit') && renderForm()}
            {mode === 'show' && renderShow()}
            {historyOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
                    <div style={{ width: 'min(920px, 96%)', maxHeight: '80vh', overflow: 'auto', background: '#fff', borderRadius: 12, padding: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0 }}>Historique du bien</h3>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button style={css.btnSecondary} onClick={() => { setHistoryOpen(false); setHistoryData([]); }}>Fermer</button>
                            </div>
                        </div>
                        {historyData.length === 0 ? (
                            <div style={{ padding: 20, color: '#64748b' }}>Aucun mouvement enregistré.</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={css.th}>Date</th>
                                        <th style={css.th}>Type</th>
                                        <th style={css.th}>Raison / Notes</th>
                                        <th style={css.th}>Utilisateur</th>
                                        <th style={css.thRight}>Détails</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.map((m) => (
                                        <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px' }}>{m.movement_date ? new Date(m.movement_date).toLocaleString('fr-FR') : ''}</td>
                                            <td style={{ padding: '10px 14px' }}>{m.type_label || m.type}</td>
                                            <td style={{ padding: '10px 14px' }}>{m.reason || m.notes || '—'}</td>
                                            <td style={{ padding: '10px 14px' }}>{m.user ? m.user.name : '—'}</td>
                                            <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                                {m.changes ? JSON.stringify(m.changes) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
            {newCategoryOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
                    <div style={{ width: 'min(400px, 96%)', background: '#fff', borderRadius: 12, padding: 24 }}>
                        <h3 style={{ margin: '0 0 16px 0' }}>Ajouter une catégorie</h3>
                        <input
                            style={{ ...css.input, marginBottom: 16 }}
                            type="text"
                            placeholder="Nom de la catégorie"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && createNewCategory()}
                        />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button style={css.btnSecondary} onClick={() => { setNewCategoryOpen(false); setNewCategoryName(''); }} disabled={loading}>
                                Annuler
                            </button>
                            <button style={css.btnPrimary} onClick={createNewCategory} disabled={loading}>
                                {loading ? '⏳ Création…' : '✓ Créer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}